// @givmo/board — recording somebody who served before this board used Givmo.
//
// ══ WHY THIS IS NOT "ADD A MEMBER, THEN END THEIR SEAT" ════════════════════════════════════════
//
// That sequence writes a removal act that never happened, stamped with today's date, onto a store
// whose whole premise is that it does not do that. So a former seat is entered DIRECTLY: it arrives
// already ended, with `seat_provenance = 'imported'`, and a CHECK ties the two together. That is also
// what makes "a former seat never holds a credential" structural rather than a rule somebody has to
// remember — every access grant re-reads the seat's own standing on use, and this seat has been ended
// from the moment it existed.
//
// ⚠ TWO CLOCKS, AND THEY NEVER MERGE. `served_from`/`served_to` are the ORGANIZATION's account of a term;
// `recorded_at` is the module's account of when it was told. An organization that did not record an end
// date has not recorded one, and substituting the day we were told would put a date in their mouth
// undetectably — so BOTH term dates are optional, and neither is ever filled in from the other. A
// organization working from one undated roster page knows that somebody served and not when, and refusing
// the record would lose the fact they do have.
//
// There is deliberately NO duplicate check. The roster's fence is a partial unique index over ACTIVE
// seats, so recording a sitting director's earlier term — or two earlier terms for one person — is
// ordinary. That is what a board with re-appointments looks like.
//
// THE TWO FACTS (CBG-YR3-ROLES layer 1) are the member dialog's: Director, yes or no, and the board's
// titles. A title ticked here is HISTORY — the backend stores it ended, with the "Served to" date as
// when it was held to and no claim about when it began, which is what the dialog's line promises. A
// former member need not have been a director, so the dialog says "someone".

import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { isGivmoError } from '@givmo/api-client';
import { Button, Dialog, ErrorMessage, TextField, toast } from '@givmo/design-system';
import { FieldNote } from '../../components/field-note';
import { useBoardOperations } from '../../board-runtime';
import { DirectorField, TitlesField, useOfficerTitleCatalog } from './officer-facts-fields';
import { titleRefs } from './officer-roles';
import { officerRolesWords as w } from './officer-roles-words';

export interface HistoricalSeatDialogProps {
  orgRef: string;
  onClose: () => void;
  onSaved: () => void;
}

export function HistoricalSeatDialog({ orgRef, onClose, onSaved }: HistoricalSeatDialogProps) {
  const operations = useBoardOperations();
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [director, setDirector] = useState(true);
  const titleCatalog = useOfficerTitleCatalog(orgRef);
  const [titleKeys, setTitleKeys] = useState<string[]>([]);
  const [servedFrom, setServedFrom] = useState('');
  const [servedTo, setServedTo] = useState('');
  const [error, setError] = useState<string | null>(null);

  const record = useMutation({
    mutationFn: async () => {
      await operations.officerRecordHistoricalBoardMember({
        path: { org_ref: orgRef },
        body: {
          display_name: displayName.trim(),
          email_of_record: email.trim(),
          is_director: director,
          ...(titleCatalog.state === 'ready'
            ? { titles: titleRefs(titleCatalog.catalog, titleKeys) }
            : {}),
          // Omitted rather than blanked when the organization has no date to give.
          ...(servedFrom.length > 0 ? { served_from: servedFrom } : {}),
          ...(servedTo.length > 0 ? { served_to: servedTo } : {}),
        },
        throwOnError: true,
      });
    },
    onSuccess: () => {
      toast.success('Recorded as a former member.');
      onSaved();
    },
    onError: (caught) =>
      setError(
        isGivmoError(caught) ? caught.message : 'We couldn’t record that. Please try again.',
      ),
  });

  const canSave = displayName.trim().length > 0 && email.trim().length > 0 && !record.isPending;

  return (
    <Dialog.Root open onOpenChange={(next) => !next && !record.isPending && onClose()}>
      <Dialog.Content
        aria-label="Record someone who served before"
        className="flex max-h-[80vh] flex-col gap-16 overflow-y-auto"
        testId="roster-historical-dialog"
      >
        <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
          Record someone who served before
        </Dialog.Title>

        <p className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary">
          {w.formerIntro}
        </p>

        <TextField
          label="Name"
          value={displayName}
          maxLength={200}
          onChange={(event) => setDisplayName(event.target.value)}
          testId="roster-historical-name"
        />

        <TextField
          label="Email of record"
          type="email"
          value={email}
          maxLength={320}
          onChange={(event) => setEmail(event.target.value)}
          testId="roster-historical-email"
        />
        <FieldNote>
          The address your records have for them. It is kept with the seat and never written to.
        </FieldNote>

        <DirectorField
          value={director}
          onChange={setDirector}
          disabled={record.isPending}
          testIdPrefix="roster-historical"
        />

        <TitlesField
          state={titleCatalog.state}
          choices={titleCatalog.catalog}
          selected={titleKeys}
          onChange={setTitleKeys}
          disabled={record.isPending}
          note={w.formerTitlesEnd}
          unavailableLine={w.titlesUnavailableNew}
          testIdPrefix="roster-historical"
        />

        <div className="grid grid-cols-2 gap-12">
          <TextField
            label="Served from (optional)"
            type="date"
            value={servedFrom}
            onChange={(event) => setServedFrom(event.target.value)}
            testId="roster-historical-from"
          />
          <TextField
            label="Served to (optional)"
            type="date"
            value={servedTo}
            onChange={(event) => setServedTo(event.target.value)}
            testId="roster-historical-to"
          />
        </div>
        <FieldNote>
          Your board’s own account of the term. Leave either blank if you don’t have it — Givmo will
          record the day you told us separately, and it will never stand in for a date you didn’t
          give.
        </FieldNote>

        {error !== null ? (
          <ErrorMessage testId="roster-historical-error">{error}</ErrorMessage>
        ) : null}

        <div className="flex justify-end gap-10">
          <Button
            appearance="saas"
            tone="secondary"
            disabled={record.isPending}
            onClick={onClose}
            testId="roster-historical-cancel"
          >
            Cancel
          </Button>
          <Button
            appearance="saas"
            tone="primary"
            loading={record.isPending}
            disabled={!canSave}
            onClick={() => {
              setError(null);
              record.mutate();
            }}
            testId="roster-historical-save"
          >
            Record them
          </Button>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
}
